import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useVideos } from "../../hooks/useVideos";
import FilmPlayer from "./FilmPlayer";

export default function VideoList() {
  const { videos, loading } = useVideos();
  const [searchParams] = useSearchParams();
  const targetVideoId = searchParams.get("v");
  const [shown, setShown] = useState(3);
  const sentinelRef = useRef(null);
  const targetRef = useRef(null);

  useEffect(() => {
    if (!targetVideoId || videos.length === 0) {
      return;
    }

    const targetIndex = videos.findIndex((film) => film.youtubeVideoId === targetVideoId);
    if (targetIndex >= 0) {
      setShown((current) => Math.max(current, targetIndex + 1));
    }
  }, [targetVideoId, videos]);

  useEffect(() => {
    if (!targetVideoId || !targetRef.current) {
      return;
    }

    const timer = window.setTimeout(() => {
      targetRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [targetVideoId, videos, shown]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !("IntersectionObserver" in window)) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setShown((current) => Math.min(videos.length, current + 2));
          }
        });
      },
      { rootMargin: "600px 0px" }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [videos.length]);

  const visibleFilms = videos.slice(0, shown);
  const allShown = shown >= videos.length;

  if (loading) {
    return (
      <section className="videos-page" aria-busy="true" aria-label="Loading films">
        <div className="videos-page__intro">
          <div className="videos-page__eyebrow">VIDEO</div>
          <div className="videos-page__header">
            <h1 className="videos-page__title">Films</h1>
          </div>
        </div>
      </section>
    );
  }

  if (videos.length === 0) {
    return (
      <section className="videos-page" aria-label="Films">
        <div className="videos-page__intro">
          <div className="videos-page__eyebrow">VIDEO</div>
          <div className="videos-page__header">
            <h1 className="videos-page__title">Films</h1>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="videos-page" aria-label="Films">
      <div className="videos-page__intro">
        <div className="videos-page__eyebrow">VIDEO · {videos.length} FILMS</div>
        <div className="videos-page__header">
          <h1 className="videos-page__title">Films</h1>
          <p className="videos-page__blurb">
            Motion work from the field and the studio. Press play — everything runs
            right here, no detours.
          </p>
        </div>
      </div>

      <div className="videos-page__stack">
        {visibleFilms.map((film, index) => {
          const isTarget = targetVideoId && film.youtubeVideoId === targetVideoId;
          return (
            <div key={film.id} ref={isTarget ? targetRef : null}>
              <FilmPlayer
                film={film}
                featured={index === 0}
                index={index}
                autoPlay={Boolean(isTarget)}
              />
            </div>
          );
        })}
      </div>

      <div ref={sentinelRef} className="videos-page__sentinel" aria-hidden="true" />

      {!allShown && (
        <div className="videos-page__hint">
          <span>SCROLL FOR MORE</span>
        </div>
      )}
    </section>
  );
}
